import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "../context/LanguageContext";
import { useAnnouncementContext } from "../context/AnnouncementContext";
import { DEAL_GUIDE_ATTR, dealGuideSeen, markDealGuideSeen } from "../utils/dealGuideSeen";

/**
 * "How to read a deal" (mirrors pinto's DealCardGuide): the first time a
 * subscriber lands on their deal grid, everything but the first card fades and
 * labels point at its parts. "Got it", the card, the scrim or Escape closes it
 * for good for this account on this browser. It replaces the old auto-started
 * multi-step tour and never shows on top of the announcement popup.
 *
 * Cards opt in with `data-deal-guide` attributes: "card" on the card root and
 * "discount" / "price" / "store" / "stock" on its parts. The first card in the
 * document is the one highlighted.
 */
type Part = "discount" | "price" | "store" | "stock";
type Rect = { x: number; y: number; w: number; h: number };

const PARTS: { part: Part; label: string }[] = [
  { part: "discount", label: "emptyStates.guide.markdown" },
  { part: "price", label: "emptyStates.guide.price" },
  { part: "store", label: "emptyStates.guide.store" },
  { part: "stock", label: "emptyStates.guide.stock" },
];
const LABEL_H = 26;
const LABEL_GAP = 6;
const PAD = 4;
const RADIUS = 20;

function rectOf(el: Element | null): Rect | null {
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0 ? { x: r.left, y: r.top, w: r.width, h: r.height } : null;
}

function measureFirstCard(): { card: Rect; parts: Partial<Record<Part, Rect>> } | null {
  const cardEl = document.querySelector(`[${DEAL_GUIDE_ATTR}="card"]`);
  const card = rectOf(cardEl);
  if (!cardEl || !card) return null;
  const onScreen = card.y >= 0 && card.y + Math.min(card.h, 160) <= window.innerHeight;
  if (!onScreen) return null;
  const parts: Partial<Record<Part, Rect>> = {};
  for (const { part } of PARTS) {
    const rect = rectOf(cardEl.querySelector(`[${DEAL_GUIDE_ATTR}="${part}"]`));
    if (rect) parts[part] = rect;
  }
  return { card, parts };
}

export default function DealCardGuide({ userId, ready }: { userId?: string | null; ready: boolean }) {
  const { t } = useTranslation();
  const { showPopupAnnouncement } = useAnnouncementContext();
  // Never on top of the announcement popup: wait (or step aside) while it is up.
  const canShow = ready && !!userId && !showPopupAnnouncement;
  const [seenFor, setSeenFor] = useState<{ userId: string; seen: boolean } | null>(null);
  const [layout, setLayout] = useState<ReturnType<typeof measureFirstCard>>(null);
  const [viewport, setViewport] = useState(() => ({ w: window.innerWidth, h: window.innerHeight }));

  const seen = userId ? (seenFor?.userId === userId ? seenFor.seen : dealGuideSeen(userId)) : true;

  // The grid can lay out a beat after the data arrives: keep trying for a few
  // seconds until the first card has a real on-screen position. Runs again
  // whenever the guide becomes allowed (e.g. after the announcement closes).
  useEffect(() => {
    if (!canShow || seen) return;
    let cancelled = false;
    let attempt = 0;
    let timer: ReturnType<typeof setTimeout>;
    const tryMeasure = () => {
      if (cancelled) return;
      attempt += 1;
      const next = measureFirstCard();
      if (next) {
        setViewport({ w: window.innerWidth, h: window.innerHeight });
        setLayout(next);
      } else if (attempt < 10) {
        timer = setTimeout(tryMeasure, 500);
      }
    };
    timer = setTimeout(tryMeasure, 700);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [canShow, seen]);

  const close = useCallback(() => {
    if (!userId) return;
    markDealGuideSeen(userId);
    setSeenFor({ userId, seen: true });
    setLayout(null);
  }, [userId]);

  const showing = canShow && !!layout && !seen;

  useEffect(() => {
    if (!showing) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    const onResize = () => {
      const next = measureFirstCard();
      setViewport({ w: window.innerWidth, h: window.innerHeight });
      if (next) setLayout(next);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onResize);
    };
  }, [showing, close]);

  if (!showing || !layout) return null;

  const { card, parts } = layout;
  const { w: vw, h: vh } = viewport;
  const hole = { x: card.x - PAD, y: card.y - PAD, w: card.w + PAD * 2, h: card.h + PAD * 2 };
  const labelX = hole.x + hole.w + 12;
  const labelMaxW = vw - labelX - 8;
  // No room beside the card (a one-column grid): labels sit on the card's right edge.
  const labelsInside = labelMaxW < 110;

  // Labels sit beside the card at each part's height, nudged apart if two collide.
  const labels = PARTS.reduce<{ part: Part; text: string; targetY: number; labelY: number; dotX: number }[]>(
    (placed, { part, label }) => {
      const rect = parts[part];
      if (!rect) return placed;
      const targetY = rect.y + rect.h / 2;
      const previous = placed[placed.length - 1];
      const labelY = Math.max(targetY - LABEL_H / 2, previous ? previous.labelY + LABEL_H + LABEL_GAP : -Infinity);
      const dotX = Math.min(rect.x + rect.w + 4, card.x + card.w - 8);
      return [...placed, { part, text: t(label), targetY, labelY, dotX }];
    },
    []
  );

  const headingAbove = hole.y - 92 >= 12;
  const footerTop = hole.y + hole.h + 18;
  const footerFits = footerTop + 110 <= vh;
  const blockLeft = Math.max(16, Math.min(hole.x, vw - 316));
  const blockWidth = Math.min(vw - blockLeft - 16, 420);

  const r = RADIUS;
  const holePath =
    `M${hole.x + r},${hole.y} H${hole.x + hole.w - r} Q${hole.x + hole.w},${hole.y} ${hole.x + hole.w},${hole.y + r} ` +
    `V${hole.y + hole.h - r} Q${hole.x + hole.w},${hole.y + hole.h} ${hole.x + hole.w - r},${hole.y + hole.h} ` +
    `H${hole.x + r} Q${hole.x},${hole.y + hole.h} ${hole.x},${hole.y + hole.h - r} V${hole.y + r} Q${hole.x},${hole.y} ${hole.x + r},${hole.y} Z`;

  return createPortal(
    <div
      className="fixed inset-0 z-[9990]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="deal-card-guide-title"
      data-testid="deal-card-guide"
    >
      <svg className="absolute inset-0 h-full w-full" width={vw} height={vh} onClick={close} aria-hidden="true">
        <path
          d={`M0,0 H${vw} V${vh} H0 Z ${holePath}`}
          fillRule="evenodd"
          className="fill-[rgba(247,245,250,0.93)] dark:fill-[rgba(20,18,23,0.9)]"
        />
      </svg>
      <button
        type="button"
        aria-label={t("emptyStates.guide.gotIt")}
        onClick={close}
        className="absolute cursor-pointer"
        style={{ left: hole.x, top: hole.y, width: hole.w, height: hole.h, borderRadius: r }}
      />

      <div
        className="absolute flex flex-col gap-[3px]"
        style={{ top: headingAbove ? Math.max(12, hole.y - 92) : footerTop, left: blockLeft, width: blockWidth }}
      >
        <h2 id="deal-card-guide-title" className="text-2xl font-black leading-[29px] tracking-tight text-[#1e1b3a] dark:text-white">
          {t("emptyStates.guide.title")}
        </h2>
        <p className="text-sm font-bold leading-[19px] text-[#6b6585] dark:text-[#b3adc7]">{t("emptyStates.guide.sub")}</p>
      </div>

      {labels.map(({ part, text, targetY, labelY, dotX }) => (
        <div key={part} className="pointer-events-none" data-guide-label={part}>
          {!labelsInside && (
            <span
              className="absolute h-0.5 bg-violet-400"
              style={{ left: dotX, top: targetY - 1, width: Math.max(0, labelX - dotX) }}
            />
          )}
          <span className="absolute h-2 w-2 rounded-full bg-violet-600" style={{ left: dotX - 4, top: targetY - 4 }} />
          <span
            className="absolute flex items-center truncate rounded-full bg-violet-600 px-[11px] text-[12.5px] font-black text-white shadow-sm"
            style={
              labelsInside
                ? { right: vw - (card.x + card.w) + 14, top: labelY, height: LABEL_H, maxWidth: card.w - 28 }
                : { left: labelX, top: labelY, height: LABEL_H, maxWidth: labelMaxW }
            }
          >
            {text}
          </span>
        </div>
      ))}

      <div
        className="absolute flex flex-col items-center gap-2.5 text-center"
        style={
          footerFits && headingAbove
            ? { top: footerTop, left: blockLeft, width: Math.max(Math.min(blockWidth, 360), Math.min(hole.w, blockWidth)) }
            : { bottom: 24, left: 16, right: 16 }
        }
      >
        <p className="text-sm font-bold leading-[19px] text-[#4a4560] dark:text-[#e4dff0]">{t("emptyStates.guide.tap")}</p>
        <button
          type="button"
          onClick={close}
          autoFocus
          className="h-12 rounded-full bg-violet-600 px-[30px] text-base font-black text-white shadow-[0_8px_22px_rgba(124,58,237,0.3)] transition-colors hover:bg-violet-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 focus-visible:ring-offset-2"
        >
          {t("emptyStates.guide.gotIt")}
        </button>
      </div>
    </div>,
    document.body
  );
}
